import React, { useState, useEffect } from "react";
import {
    useGetClinicalNote,
    useCreateClinicalNote,
    useUpdateClinicalNote,
    useDeleteClinicalNote,
} from "../api/clinicalNotes";
import { Alert } from "antd";
import showToast from "../../utils/toast";
import { useInvalidateAppointments } from "../api/app";

const ClinicalNotesModal = ({ handleClose, id, appointmentId }) => {
    const { data: noteData, isLoading: isFetching } =
        useGetClinicalNote(appointmentId);
    const createNote = useCreateClinicalNote();
    const updateNote = useUpdateClinicalNote();
    const deleteNote = useDeleteClinicalNote();
    const invalidate = useInvalidateAppointments();

    const [formData, setFormData] = useState({
        observations: "",
        diagnosis: "",
        notes: "",
        complaints: "",
        investigations: "",
        appointmentId: appointmentId || "",
        psychiatricHistory: "",
        presentIllness: "",
        treatmentHistory: "",
        medicalHistory: "",
        familyHistory: "",
    });

    const isEditMode = !!noteData;

    useEffect(() => {
        if (noteData) {
            setFormData({
                observations: noteData.observations || "",
                diagnosis: noteData.diagnosis || "",
                notes: noteData.notes || "",
                complaints: noteData.complaints || "",
                investigations: noteData.investigations || "",
                appointmentId: noteData.appointmentId || appointmentId || "",
                presentIllness: noteData?.presentIllness || "",
                psychiatricHistory: noteData?.psychiatricHistory || "",
                treatmentHistory: noteData?.treatmentHistory || "",
                medicalHistory: noteData?.medicalHistory || "",
                familyHistory: noteData?.familyHistory || "",
            });
        }
    }, [noteData, appointmentId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = { ...formData };

        if (!formData.appointmentId) {
            showToast("error", "Appointment ID is required.");
            return;
        }

        if (isEditMode) {
            updateNote.mutate(
                { appointmentId, data: payload },
                {
                    onSuccess: () => {
                        showToast(
                            "success",
                            "Clinical note updated successfully."
                        );
                        handleClose();
                    },
                    onError: () => showToast("error", "Update failed."),
                }
            );
        } else {
            createNote.mutate(payload, {
                onSuccess: () => {
                    showToast("success", "Clinical note created.");
                    handleClose();
                },
                onError: () => showToast("error", "Creation failed."),
            });
        }
        invalidate();
    };

    const handleDelete = () => {
        if (window.confirm("Are you sure you want to delete this note?")) {
            deleteNote.mutate(appointmentId, {
                onSuccess: () => {
                    showToast("success", "Note deleted.");
                    handleClose();
                },
                onError: () => showToast("error", "Deletion failed."),
            });
        }

        invalidate();
    };

    return (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                backdropFilter: "blur(8px)",
                zIndex: 1050,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "20px",
            }}
        >
            <div
                style={{
                    backgroundColor: "white",
                    borderRadius: "20px",
                    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
                    width: "100%",
                    maxWidth: "900px",
                    maxHeight: "90vh",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div
                    style={{
                        background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        color: "white",
                        padding: "24px 30px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                        }}
                    >
                        <div
                            style={{
                                width: "40px",
                                height: "40px",
                                backgroundColor: "rgba(255, 255, 255, 0.2)",
                                borderRadius: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "18px",
                            }}
                        >
                            📋
                        </div>
                        <h5
                            style={{
                                margin: 0,
                                fontSize: "20px",
                                fontWeight: "600",
                                color: "white",
                            }}
                        >
                            {isEditMode
                                ? "Edit Clinical Note"
                                : "New Clinical Note"}
                        </h5>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        style={{
                            background: "rgba(255, 255, 255, 0.2)",
                            border: "none",
                            borderRadius: "12px",
                            width: "40px",
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontSize: "18px",
                            cursor: "pointer",
                        }}
                    >
                        ✕
                    </button>
                </div>

                {/* Body */}
                <div style={{ padding: "30px", overflowY: "auto", flex: 1 }}>
                    {isFetching && isEditMode && (
                        <div style={{ marginBottom: "20px" }}>
                            <Alert message="Loading note data..." type="info" />
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="row">
                            <div className="col-md-6">
                                <div style={{ marginBottom: "24px" }}>
                                    <label
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            marginBottom: "8px",
                                            fontSize: "14px",
                                            fontWeight: "600",
                                            color: "#4a5568",
                                        }}
                                    >
                                        💬 Complaints
                                    </label>
                                    <textarea
                                        name="complaints"
                                        value={formData.complaints}
                                        onChange={handleChange}
                                        rows="4"
                                        style={{
                                            width: "100%",
                                            borderRadius: "12px",
                                            border: "2px solid #e2e8f0",
                                            padding: "12px 16px",
                                            fontSize: "14px",
                                            resize: "vertical",
                                            fontFamily: "inherit",
                                        }}
                                    />
                                </div>

                                <div style={{ marginBottom: "24px" }}>
                                    <label
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            marginBottom: "8px",
                                            fontSize: "14px",
                                            fontWeight: "600",
                                            color: "#4a5568",
                                        }}
                                    >
                                        ⏱️ Duration
                                    </label>
                                    <input
                                        type="text"
                                        name="investigations"
                                        value={formData.investigations}
                                        onChange={handleChange}
                                        placeholder="e.g. 4 week(s)"
                                        style={{
                                            width: "100%",
                                            borderRadius: "12px",
                                            border: "2px solid #e2e8f0",
                                            padding: "12px 16px",
                                            fontSize: "14px",
                                            fontFamily: "inherit",
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div style={{ marginBottom: "24px" }}>
                                    <label
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            marginBottom: "8px",
                                            fontSize: "14px",
                                            fontWeight: "600",
                                            color: "#4a5568",
                                        }}
                                    >
                                        🤒 History of Present Illness
                                    </label>
                                    <textarea
                                        name="presentIllness"
                                        value={formData.presentIllness}
                                        onChange={handleChange}
                                        rows="4"
                                        style={{
                                            width: "100%",
                                            borderRadius: "12px",
                                            border: "2px solid #e2e8f0",
                                            padding: "12px 16px",
                                            fontSize: "14px",
                                            resize: "vertical",
                                            fontFamily: "inherit",
                                        }}
                                    />
                                </div>
                                <div style={{ marginBottom: "24px" }}>
                                    <label
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            marginBottom: "8px",
                                            fontSize: "14px",
                                            fontWeight: "600",
                                            color: "#4a5568",
                                        }}
                                    >
                                        💊 Treatment History
                                    </label>
                                    <textarea
                                        name="treatmentHistory"
                                        value={formData.treatmentHistory}
                                        onChange={handleChange}
                                        rows="4"
                                        style={{
                                            width: "100%",
                                            borderRadius: "12px",
                                            border: "2px solid #e2e8f0",
                                            padding: "12px 16px",
                                            fontSize: "14px",
                                            resize: "vertical",
                                            fontFamily: "inherit",
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div style={{ marginBottom: "24px" }}>
                                    <label
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            marginBottom: "8px",
                                            fontSize: "14px",
                                            fontWeight: "600",
                                            color: "#4a5568",
                                        }}
                                    >
                                        👨‍👩‍👧Past Psychiatric History
                                    </label>
                                    <textarea
                                        name="psychiatricHistory"
                                        value={formData.psychiatricHistory}
                                        onChange={handleChange}
                                        rows="4"
                                        style={{
                                            width: "100%",
                                            borderRadius: "12px",
                                            border: "2px solid #e2e8f0",
                                            padding: "12px 16px",
                                            fontSize: "14px",
                                            resize: "vertical",
                                            fontFamily: "inherit",
                                        }}
                                    />
                                </div>
                                <div style={{ marginBottom: "24px" }}>
                                    <label
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            marginBottom: "8px",
                                            fontSize: "14px",
                                            fontWeight: "600",
                                            color: "#4a5568",
                                        }}
                                    >
                                        👨‍👩‍👧 Family History
                                    </label>
                                    <textarea
                                        name="familyHistory"
                                        value={formData.familyHistory}
                                        onChange={handleChange}
                                        rows="4"
                                        style={{
                                            width: "100%",
                                            borderRadius: "12px",
                                            border: "2px solid #e2e8f0",
                                            padding: "12px 16px",
                                            fontSize: "14px",
                                            resize: "vertical",
                                            fontFamily: "inherit",
                                        }}
                                    />
                                </div>
                                <label
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        marginBottom: "8px",
                                        fontSize: "14px",
                                        fontWeight: "600",
                                        color: "#4a5568",
                                    }}
                                >
                                    📝 Notes
                                </label>
                                <textarea
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    rows="4"
                                    style={{
                                        width: "100%",
                                        borderRadius: "12px",
                                        border: "2px solid #e2e8f0",
                                        padding: "12px 16px",
                                        fontSize: "14px",
                                        resize: "vertical",
                                        fontFamily: "inherit",
                                    }}
                                />
                            </div>

                            <div className="col-md-6">
                                <div style={{ marginBottom: "24px" }}>
                                    <label
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            marginBottom: "8px",
                                            fontSize: "14px",
                                            fontWeight: "600",
                                            color: "#4a5568",
                                        }}
                                    >
                                        🩺 Medical History
                                    </label>
                                    <textarea
                                        name="medicalHistory"
                                        value={formData.medicalHistory}
                                        onChange={handleChange}
                                        rows="4"
                                        style={{
                                            width: "100%",
                                            borderRadius: "12px",
                                            border: "2px solid #e2e8f0",
                                            padding: "12px 16px",
                                            fontSize: "14px",
                                            resize: "vertical",
                                            fontFamily: "inherit",
                                        }}
                                    />
                                </div>

                                <div style={{ marginBottom: "24px" }}>
                                    <label
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            marginBottom: "8px",
                                            fontSize: "14px",
                                            fontWeight: "600",
                                            color: "#4a5568",
                                        }}
                                    >
                                        👁️ Observations
                                    </label>
                                    <textarea
                                        name="observations"
                                        value={formData.observations}
                                        onChange={handleChange}
                                        rows="4"
                                        style={{
                                            width: "100%",
                                            borderRadius: "12px",
                                            border: "2px solid #e2e8f0",
                                            padding: "12px 16px",
                                            fontSize: "14px",
                                            resize: "vertical",
                                            fontFamily: "inherit",
                                        }}
                                    />
                                </div>
                                <div style={{ marginBottom: "24px" }}>
                                    <label
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            marginBottom: "8px",
                                            fontSize: "14px",
                                            fontWeight: "600",
                                            color: "#4a5568",
                                        }}
                                    >
                                        🩺 Prov. Diagnosis
                                    </label>
                                    <textarea
                                        name="diagnosis"
                                        value={formData.diagnosis}
                                        onChange={handleChange}
                                        rows="4"
                                        style={{
                                            width: "100%",
                                            borderRadius: "12px",
                                            border: "2px solid #e2e8f0",
                                            padding: "12px 16px",
                                            fontSize: "14px",
                                            resize: "vertical",
                                            fontFamily: "inherit",
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div
                            style={{
                                borderTop: "1px solid #e2e8f0",
                                paddingTop: "24px",
                                display: "flex",
                                gap: "12px",
                                justifyContent: "flex-end",
                            }}
                        >
                            <button
                                type="button"
                                onClick={handleClose}
                                style={{
                                    background: "#f7fafc",
                                    border: "2px solid #e2e8f0",
                                    borderRadius: "12px",
                                    padding: "12px 24px",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#4a5568",
                                    cursor: "pointer",
                                }}
                            >
                                Cancel
                            </button>

                            {isEditMode && (
                                <button
                                    type="button"
                                    onClick={handleDelete}
                                    style={{
                                        background: "#f56565",
                                        border: "none",
                                        borderRadius: "12px",
                                        padding: "12px 24px",
                                        fontSize: "14px",
                                        fontWeight: "600",
                                        color: "white",
                                        cursor: "pointer",
                                    }}
                                >
                                    Delete
                                </button>
                            )}

                            <button
                                type="submit"
                                style={{
                                    background:
                                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                    border: "none",
                                    borderRadius: "12px",
                                    padding: "12px 24px",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "white",
                                    cursor: "pointer",
                                }}
                            >
                                {isEditMode ? "Update" : "Save"} Note
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ClinicalNotesModal;
